import { useEffect, useMemo, useState } from 'react';
import Chart from '../components/Chart.jsx';
import { fetchStaffPerformance } from '../api/staff.js';

const BRAND_COLORS = ['#1e3a8a', '#2563eb', '#38bdf8', '#f97316', '#eab308', '#94a3b8'];

function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

function formatMonthLabel() {
  return new Date().toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });
}

function ChangeBadge({ value, suffix = '% vs last period' }) {
  const positive = value >= 0;
  return (
    <p className={`mt-1 text-[10px] font-medium leading-tight ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
      {positive ? '↑' : '↓'} {Math.abs(value)}
      {suffix}
    </p>
  );
}

function MetricCard({ titleLines, value, change, changeSuffix, compact }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <p className={`font-medium uppercase tracking-wide text-slate-500 leading-tight ${compact ? 'text-[10px]' : 'text-xs'}`}>
        {titleLines.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </p>
      <p className={`mt-1 font-bold tabular-nums text-slate-800 ${compact ? 'text-lg' : 'text-2xl'}`}>
        {value}
      </p>
      {change !== undefined ? <ChangeBadge value={change} suffix={changeSuffix} /> : null}
    </div>
  );
}

function SurveyData() {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      const { startISO, endISO } = currentMonthRange();
      const result = await fetchStaffPerformance(startISO, endISO);
      setLoading(false);
      if (!result.ok) {
        setError(result.error || 'Failed to load performance data');
        return;
      }
      setPerformance(result.performance);
    })();
  }, []);

  const trendChart = useMemo(() => {
    const labels = performance?.surveyTrend.map((row) => row.date) ?? [];
    const data = performance?.surveyTrend.map((row) => row.count) ?? [];
    return {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Surveys',
            data,
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.08)',
            tension: 0.45,
            cubicInterpolationMode: 'monotone',
            fill: true,
            pointRadius: 3,
            pointBackgroundColor: '#2563eb',
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, datalabels: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
        },
      },
    };
  }, [performance?.surveyTrend]);

  const brandChart = useMemo(() => {
    const brands = performance?.topBrands ?? [];
    const total = brands.reduce((sum, row) => sum + row.count, 0);
    const labels = brands.map((row) => row.brand);
    const data = brands.map((row) => row.count);
    const legend = brands.map((row, index) => ({
      label: row.brand,
      color: BRAND_COLORS[index % BRAND_COLORS.length],
      pct: total > 0 ? Math.round((row.count / total) * 100) : 0,
    }));
    return {
      total,
      legend,
      config: {
        type: 'doughnut',
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor: BRAND_COLORS.slice(0, labels.length),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '68%',
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
          },
        },
      },
    };
  }, [performance?.topBrands]);

  const weeklyChart = useMemo(() => {
    const rows = performance?.weeklyPerformance ?? [];
    return {
      type: 'bar',
      data: {
        labels: rows.map((row) => row.week),
        datasets: [
          {
            label: 'Surveys',
            data: rows.map((row) => row.surveys),
            backgroundColor: '#2563eb',
            borderRadius: 4,
            barThickness: 10,
          },
          {
            label: 'QR Scans',
            data: rows.map((row) => row.qrScans),
            backgroundColor: '#10b981',
            borderRadius: 4,
            barThickness: 10,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 8 } },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: { boxWidth: 10, font: { size: 10 } },
          },
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#64748b',
            font: { size: 9 },
            clip: false,
            offset: 2,
          },
        },
        datasets: {
          bar: {
            barPercentage: 1,
            categoryPercentage: 0.2,
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 10 } } },
          y: {
            beginAtZero: true,
            grace: '15%',
            ticks: { precision: 0, font: { size: 10 } },
          },
        },
      },
    };
  }, [performance?.weeklyPerformance]);

  if (loading) {
    return <p className="text-center text-slate-500">Loading performance…</p>;
  }

  if (error) {
    return <p className="text-center text-red-600">{error}</p>;
  }

  return (
    <div className="space-y-4 pb-2">
      <p className="text-center text-xs text-slate-500">{formatMonthLabel()}</p>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard
          compact
          titleLines={['Total', 'Surveys']}
          value={(performance?.totalSurveys ?? 0).toLocaleString()}
          change={performance?.changes.totalSurveys}
        />
        <MetricCard
          compact
          titleLines={['Unique', 'Consumers']}
          value={(performance?.uniqueConsumers ?? 0).toLocaleString()}
          change={performance?.changes.uniqueConsumers}
        />
        <MetricCard
          compact
          titleLines={['Conversion', 'Rate']}
          value={`${performance?.conversionRate ?? 0}%`}
          change={performance?.changes.conversionRate}
          changeSuffix=" pts vs last period"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Survey Submission Trend</h3>
        <div className="relative h-52">
          {performance && performance.surveyTrend.length > 0 ? (
            <Chart {...trendChart} height="auto" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              No survey data for this period
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Top Brands</h3>
        <div className="relative flex min-h-[13rem] items-center">
          {performance && brandChart.total > 0 ? (
            <>
              <div className="relative h-52 w-[58%] min-w-0">
                <Chart {...brandChart.config} height="auto" />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-lg font-bold tabular-nums text-slate-800">{brandChart.total}</p>
                    <p className="text-[10px] text-slate-500">Surveys</p>
                  </div>
                </div>
              </div>
              <div className="flex w-[42%] flex-col justify-center gap-1.5 pl-2">
                {brandChart.legend.map((item) => (
                  <div key={item.label} className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="truncate text-[10px] text-slate-600">
                      {item.label}: {item.pct}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-52 w-full items-center justify-center text-sm text-slate-400">
              No brand data for this period
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Performance vs Last Month</h3>
        <div className="relative h-60">
          {performance && performance.weeklyPerformance.length > 0 ? (
            <Chart {...weeklyChart} height="auto" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              No weekly data for this period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SurveyData;
